class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', this.onRecommendationSubmit.bind(this));

    this.setHeaderCartIconAccessibility();
    this.observePrimaryCartBubble();
    this.bindCustomProductForms();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .ss-cart');

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.ssCartDrawerBound === '1') return;
      cartLink.dataset.ssCartDrawerBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  bindCustomProductForms() {
    if (document.documentElement.dataset.ssCartProductFormsBound === '1') return;
    document.documentElement.dataset.ssCartProductFormsBound = '1';

    // Only custom product cards use this capture handler. Product pages keep the
    // native <product-form> flow so variant changes and availability stay in sync.
    const productFormSelector = 'form.ss-fc__form, form.ss-cg-card__form';

    document.addEventListener(
      'submit',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLFormElement)) return;

        const form = target.matches(productFormSelector)
          ? target
          : target.closest(productFormSelector);
        if (!form) return;

        event.preventDefault();
        event.stopPropagation();

        this.onCustomProductSubmit(form, event.submitter);
      },
      true
    );
  }

  async onCustomProductSubmit(form, submitter) {
    const button = submitter || form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;

    this.setActiveElement(button);

    const label = button.querySelector('span:not(.loading__spinner)');
    const originalLabel = label?.textContent || '';
    const spinner = button.querySelector('.loading__spinner');

    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    try {
      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      formData.append('sections', this.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'Produsul nu a putut fi adăugat.');
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ss-product-card',
          productVariantId: formData.get('id'),
          cartData: state,
        });
      }

      this.renderContents(state);
    } catch (error) {
      console.error(error);
      if (label) {
        label.textContent = 'ÎNCEARCĂ DIN NOU';
        setTimeout(() => {
          label.textContent = originalLabel;
        }, 1600);
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    requestAnimationFrame(() => {
      this.classList.add('animate');
      requestAnimationFrame(() => this.classList.add('active'));
    });

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();

    setTimeout(() => {
      const containerToTrapFocusOn = document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
      if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, 380);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);

    setTimeout(() => {
      if (!this.classList.contains('active')) this.classList.remove('animate');
    }, 350);
  }

  renderContents(parsedState) {
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      this.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
      this.syncSecondaryCartIcons();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async onRecommendationSubmit(event) {
    const form = event.target.closest('[data-cart-recommendation-form]');
    if (!form) return;

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'SE ADAUGĂ...';

    try {
      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      formData.append('sections', this.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'Produsul nu a putut fi adăugat.');
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendation',
          productVariantId: formData.get('id'),
          cartData: state,
        });
      }

      this.renderContents(state);
    } catch (error) {
      console.error(error);
      button.textContent = 'ÎNCEARCĂ DIN NOU';
      setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 1600);
      return;
    }
  }

  observePrimaryCartBubble() {
    const primary = document.getElementById('cart-icon-bubble');
    if (!primary || typeof MutationObserver === 'undefined') return;

    this.cartIconObserver = new MutationObserver(() => this.syncSecondaryCartIcons());
    this.cartIconObserver.observe(primary, { childList: true, subtree: true, characterData: true });
    this.syncSecondaryCartIcons();
  }

  syncSecondaryCartIcons() {
    const primary = document.getElementById('cart-icon-bubble');
    if (!primary) return;

    document.querySelectorAll('.ss-cart').forEach((icon) => {
      if (icon !== primary) icon.innerHTML = primary.innerHTML;
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();

    // SSSALON explicitly re-renders the full drawer after each cart mutation.
    // Disable Dawn's extra fragment refresh to avoid two asynchronous renders racing each other.
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    const quantityInput = row?.querySelector('.quantity__input') || this.querySelector(`#Drawer-quantity-${line}`);
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const resolvedVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const errors = document.getElementById('CartDrawer-CartErrors');

    if (errors) errors.textContent = '';

    if (!lineKey) {
      console.error('SS cart: missing cart line key for line', line);
      if (errors) errors.textContent = window.cartStrings.error;
      return;
    }

    this.enableLoading(line);

    try {
      const body = JSON.stringify({
        id: lineKey,
        quantity,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body,
      });

      const state = await response.json();

      if (!response.ok || state.errors) {
        const message =
          typeof state.errors === 'string'
            ? state.errors
            : state.description || state.message || window.cartStrings.error;
        throw new Error(message);
      }

      let renderedState = state;

      if (!state.sections?.['cart-drawer']) {
        const sectionResponse = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!sectionResponse.ok) throw new Error(window.cartStrings.error);

        renderedState = {
          ...state,
          sections: await sectionResponse.json(),
        };
      }

      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') {
        throw new Error(window.cartStrings.error);
      }

      drawer.renderContents(renderedState);

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: state,
          variantId: resolvedVariantId,
        });
      }
    } catch (error) {
      console.error(error);

      if (quantityInput && this.isConnected) {
        quantityInput.value = quantityInput.getAttribute('value');
      }

      const currentErrors = document.getElementById('CartDrawer-CartErrors');
      if (currentErrors) currentErrors.textContent = error.message || window.cartStrings.error;
    } finally {
      if (this.isConnected) this.disableLoading(line);
    }
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
